// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { MappedLink } from "@insite/mobius/Menu";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FloatingTooltipStyles } from "@werner/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@werner/App/Components/Header/HeaderClickable";
import FloatingMenuTooltip from "@werner/App/Components/Menu/FloatingMenuTooltip";
import MiniMyAccount from "@werner/App/Components/MiniMyAccount/MiniMyAccount";
import MiniSignIn from "@werner/App/Components/MiniSignIn/MiniSignIn";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import MyAccount from "@werner/XDL/Icons/MyAccount";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    /* <XNG-Changes> */
    extraLinks = "extraLinks",
    navListTitle = "navListTitle",
}

/* <XNG-Changes> */
export interface MyAccountExtraLinks {
    fields: {
        destination: LinkFieldValue;
        overrideTitle: string;
        billTrust: boolean;
    };
}
/* <XNG-Changes> */
export interface XMappedLink extends MappedLink {
    billTrust?: boolean;
}

/* <XNG-Changes> */
export const mapLinks = (state: ApplicationState, widgetLinks: MyAccountExtraLinks[] = []) => {
    const myAccountLinks = [];

    for (const widgetLink of widgetLinks) {
        let mappedLink: XMappedLink | undefined;
        if (widgetLink.fields.destination.type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
            if (readonlyPageLink) {
                mappedLink = {
                    url: readonlyPageLink.url,
                    title: widgetLink.fields.overrideTitle || readonlyPageLink.title,
                };
            }
        } else if (widgetLink.fields.destination.type === "Url") {
            mappedLink = {
                url: widgetLink.fields.destination.value,
                title: widgetLink.fields.overrideTitle || widgetLink.fields.destination.value,
            };
        } else if (widgetLink.fields.destination.type === "Category") {
            const categoryState = getCategoryState(state, widgetLink.fields.destination.value);
            if (categoryState.value) {
                mappedLink = {
                    url: categoryState.value.path,
                    title: widgetLink.fields.overrideTitle || categoryState.value.shortDescription,
                };
            }
        }

        if (widgetLink.fields.billTrust) {
            const session = getSession(state);
            if (session.properties.generalBillTrustLink) {
                mappedLink = {
                    url: session.properties.generalBillTrustLink,
                    title: widgetLink.fields.overrideTitle || "Bill Trust",
                };
            }
        }
        if (!mappedLink) {
            continue;
        }

        mappedLink.billTrust = widgetLink.fields.billTrust;
        myAccountLinks.push(mappedLink);
    }
    return myAccountLinks;
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const myAccountLinks = mapLinks(state, ownProps.fields.extraLinks);
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userLabel || state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
        links: { title: ownProps.fields.navListTitle, links: myAccountLinks },
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    tippyPopup?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        /* <XNG-Changes> */
        [fields.extraLinks]: MyAccountExtraLinks[];
        [fields.navListTitle]: string;
    };
    isCompactHeaderSingIn?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    links,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    // XNG-Change
    isTabletScreen,
    isAuthenticated,
    isCompactHeaderSingIn,
}: Props) => {
    const [isPopupShown, setIsPopupShown] = React.useState(false);

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);
    if (!myAccountPageLink) {
        return null;
    }
    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;

    // const showIcon = visibilityState === "both" || visibilityState === "icon";
    // const showLabel = visibilityState === "both" || visibilityState === "label";

    // if (fields.icon === "User") {
    icon = MyAccount;
    // }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as XMappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): XMappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const filteredLinks = links.links.filter(link => menuItems?.some(m => m.url === link.url) || link.billTrust);
    const miniAccountContent = {
        ...links,
        links: filteredLinks,
    };

    const onSignInHandler = (e: React.MouseEvent) => {
        e.preventDefault();
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }

        // XNG-Changes
        if (isAuthenticated) {
            history.push(myAccountPageLink?.url || "/MyAccount/SignIn");
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    // let signInStatusText = translate("Sign In");
    // if (userName && !currentUserIsGuest) {
    //     signInStatusText = userName;
    // }

    // XNG-Changes
    const myAccountClickable = (
        <HeaderClickable
            iconProps={{ src: icon }}
            onClick={onSignInHandler}
            title={`${translate("Hello")},`}
            subTitle={userName}
            isOpen={isPopupShown}
        />
    );

    const signInClickable = (
        <HeaderClickable
            iconProps={{ src: icon }}
            onClick={onSignInHandler}
            title={translate("Sign In")}
            subTitle={translate("Or Register")}
            isOpen={isPopupShown}
        />
    );

    if (isTabletScreen) {
        return signInClickable;
    }

    return (
        <FloatingMenuTooltip
            extendedStyles={styles.tippyPopup}
            isOpen={isPopupShown}
            triggerComponent={isAuthenticated ? myAccountClickable : signInClickable}
            maxWidth={isAuthenticated ? 630 : 400}
            showArrow={true}
            placement={isCompactHeaderSingIn ? "bottom" : "bottom-start"}
            onOpenChange={isOpen => setIsPopupShown(isOpen)}
        >
            {isAuthenticated ? (
                <MiniMyAccount
                    myAccountLinks={miniAccountContent}
                    onSignOutHandler={onSignOutHandler}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    isPunchOutSession={isPunchOutSession}
                />
            ) : (
                <MiniSignIn />
            )}
        </FloatingMenuTooltip>
    );
};

/* <XNG-Changes> */
export const linksFieldDefinitions = [
    {
        name: fields.navListTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Product & Management",
    },
    {
        name: fields.extraLinks,
        displayName: "Links",
        editorTemplate: "ListField",
        getDisplay: (item: HasFields, state: any) => {
            const {
                destination: { type, value },
                overrideTitle,
                billTrust,
            } = item.fields;

            if (overrideTitle) {
                return overrideTitle;
            }
            if (billTrust) {
                return overrideTitle || "Bill Trust";
            }

            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return link?.title;
            }
            if (type === "Category") {
                let displayValue = "";
                const categories = (state as any).pageEditor.categories;
                if (!categories) {
                    displayValue = "Loading...";
                } else {
                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                    if (matchingCategories.length === 0) {
                        displayValue = "Unknown";
                    } else {
                        displayValue = matchingCategories[0].shortDescription;
                    }
                }

                return displayValue;
            }
            if (type === "Url") {
                return value;
            }
            return value;
        },
        defaultValue: [],
        fieldType: "General",
        fieldDefinitions: [
            {
                name: "destination",
                displayName: "link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                isVisible: (item: { fields: { billTrust: boolean } }) => item.fields.billTrust === false,
            },
            {
                name: "overrideTitle",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "billTrust",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                isVisible: item => false,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
                isVisible: item => false,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
                isVisible: item => false,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            // XNG-Changes
            ...linksFieldDefinitions,
        ] as FieldDefinition[],
        group: "Common",
    },
};

export default widgetModule;
